import produce from 'immer';
import { Produced, Draft } from 'immer/dist/internal';
import * as React from 'react';
import { DeepReadonly } from 'ts-essentials';

export type Recipe<S, A, R = void> = (
  draft: Draft<S>,
  action: A, // Should be DeepReadonly too, but: https://github.com/microsoft/TypeScript/issues/13347
  state: DeepReadonly<S>
) => Produced<S, R> | void;

/**
 * A convenient wrapper around {@linkcode React.useReducer} which additionally wraps the state into [`immer`](https://github.com/immerjs/immer).
 *
 * Given this state and actions:
 *
 * ```
 * interface State {
 *   name: string;
 *   level: number;
 *   coins: number;
 * }
 *
 * interface LevelUp {
 *   type: 'character/levelUp';
 * }
 *
 * interface Loot {
 *   type: 'character/loot';
 * }
 *
 * type Actions = LevelUp | Loot;
 * ```
 *
 * We can define a recipe and the initial state like this:
 *
 * ```
 * const initialState: State = { name: '', level: 0, coins: 0 };
 * const recipe: Recipe<State, Actions, State> = (
 *   draft,
 *   action,
 *   readOnlyState
 * ) => {
 *   return void 0;
 * };
 * ```
 *
 * And then use them in the component:
 *
 * ```
 * const [state, dispatch] = useProducer(recipe, initialState);
 * ```
 *
 * @param recipe recipe for computing the next state
 * @param initialArg initial state (or argument to lazy-initializer)
 * @param init thunk to lazyly initialize the state
 */
function useProducer<S, A, I extends S = S>(
  recipe: Recipe<S, A>,
  initialArg: I,
  init = (arg: I): S => arg
): [S, React.Dispatch<A>] {
  const reducer = React.useMemo(
    () => (state, action) =>
      produce(state, (draft) =>
        recipe(draft, action, state as DeepReadonly<S>)
      ),
    [recipe]
  ) as React.Reducer<S, A>;

  return React.useReducer(reducer, initialArg, init);
}

/**
 * A type to make typing thunks easier. You probably want to make an associated/alias type using this.
 * For example:
 *
 * ```
 * type MyThunkCreator = ThunkCreator<MyState, MyActions>
 * ```
 *
 * :warning: **Note**: Inference is broken for trivially inferrable types (ironically), such as default arguments.
 * The type will be `unknown`, but when we manually annotate it, it complains about a trivially-inferrable type annotation...
 * I'm not sure if anything else breaks because of this, but it doesn't seem so.
 */
export type ThunkCreator<S, A> = (...args: unknown[]) => Thunk<S, A>;

export type Thunk<S, A> = (
  action: React.Dispatch<ThunkOrPlainAction<S, A>>
) => unknown;
type ThunkOrPlainAction<S, A> = Thunk<S, A> | A;

/**
 * A version of {@linkcode useProducer} that accepts thunks
 *
 * @param recipe
 * @param initialArg
 * @param init
 */
export function useProducerWithThunks<S, A, I extends S = S>(
  recipe: Recipe<S, A>,
  initialArg: I,
  init = (arg: I): S => arg
): readonly [S, Thunk<S, A>] {
  const [state, plainDispatch] = useProducer(recipe, initialArg, init);

  const thunkDispatch = React.useMemo(
    () => (action: ThunkOrPlainAction<S, A>) => {
      if (action instanceof Function) {
        action(thunkDispatch);
      } else {
        // Let React handle anything else on its own
        plainDispatch(action);
      }
    },
    [plainDispatch]
  );

  return [state, thunkDispatch] as const;
}

export default useProducer;
